import { Fragment, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { FaChevronDown, FaChevronRight, FaUserPlus } from "react-icons/fa";
import { FiSave, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import "../css/DepartmentDetail.css";
import "../css/DepartmentModule.css";
import DivisionEditor from "../components/admin/DivisionEditor";
import DataTable from "../components/ui/DataTable";
import axiosInstance from "../axiosInstance";
import { allFeatures, formatDivisionLabelWithUserCount } from "./departmentFeatures";
import {
  fetchDepartmentMenuPermissions,
  ORDER_DIVISION_FEATURE,
  ORDERS_NORMAL_DIVISION_FEATURE,
  ORDERS_VAT_DIVISION_FEATURE,
  ORDERS_MUA_HO_DIVISION_FEATURE,
  ORDERS_MUA_HO_NORMAL_DIVISION_FEATURE,
  ORDERS_MUA_HO_VAT_DIVISION_FEATURE,
  ORDER_EXCEL_EXPORT_FEATURES,
  ORDER_QUICK_UPDATE_FEATURES,
  ORDER_RECONCILE_COLUMN_FEATURES,
  CATEGORIES_DIVISION_FEATURE,
  PROVIDERS_DIVISION_FEATURE,
  PROVIDER_RATING_COMMENT_DIVISION_FEATURE,
  NOTIFY_NCC_RATING_REMINDER_FEATURE,
  NOTIFY_TRANSFER_REQUEST_INBOX_FEATURE,
  NOTIFY_TRANSFER_REQUEST_PROCESS_FEATURE,
  DEPARTMENTS_DIVISION_FEATURE,
  DEPARTMENTS_DIVISION_FEATURE_LEGACY,
  CUSTOMERS_DIVISION_FEATURE,
  PARTNERS_DIVISION_FEATURE,
  CUSTOMER_FILTER_DIVISION_FEATURES,
  CUSTOMER_FILTER_GROUP_NAMES,
  customerFilterGroupToFeature,
  migrateDivisionQuickUpdateFeatureKey,
  ORDER_QUICK_UPDATE_FEATURE_KEY_MIGRATION,
  ACCOUNTING_ALL_LEAVES,
  ACCOUNTING_LEGACY_TO_LEAVES,
  ACCOUNTING_REPORT_TAB_LEAVES,
  ACCOUNTING_DEBT_LEAVES,
  ACCOUNTING_VOUCHER_LEAVES,
  ACCOUNTING_SO_CHI_LEAVES,
  ACCOUNTING_CONFIG_LEAVES,
  ACCOUNTING_UTILITY_LEAVES,
  ACCOUNTING_BANK_USE_FEATURE,
  CSKH_DIVISION_FEATURES,
  CSKH_DIVISION_PARENT,
} from "../libs/customerCreatePermission";

const ORDER_VIEW_ONLY_FEATURES = new Set(ORDER_RECONCILE_COLUMN_FEATURES);
const ORDER_LEVEL3_FEATURES = new Set(ORDER_RECONCILE_COLUMN_FEATURES);
const ORDER_DISPLAY_COLUMN_SET = new Set(ORDER_RECONCILE_COLUMN_FEATURES);

/** Đơn thường / Đơn VAT (vận chuyển + mua hộ): luôn đủ cột Thêm / Sửa / Xóa. */
const ORDER_FULL_CRUD_CHILD_FEATURES = new Set([
  ORDERS_NORMAL_DIVISION_FEATURE,
  ORDERS_VAT_DIVISION_FEATURE,
  ORDERS_MUA_HO_NORMAL_DIVISION_FEATURE,
  ORDERS_MUA_HO_VAT_DIVISION_FEATURE,
]);

/** Kế toán: chỉ dùng TKNH — chỉ cột Xem. */
const ACCOUNTING_VIEW_ONLY_FEATURES = new Set([ACCOUNTING_BANK_USE_FEATURE]);

/** Kế toán theo tab: luôn đủ cột Xem / Thêm / Sửa / Xóa (trừ tiện ích view-only). */
const ACCOUNTING_FULL_CRUD_FEATURES = new Set(
  ACCOUNTING_ALL_LEAVES.filter((f) => !ACCOUNTING_VIEW_ONLY_FEATURES.has(f))
);

/** Xuất excel đơn hàng: chỉ cờ view (giống SP xuất Excel). */
const ORDER_EXCEL_EXPORT_ONLY_FEATURES = new Set(ORDER_EXCEL_EXPORT_FEATURES);
const ORDER_QUICK_UPDATE_ONLY_FEATURES = new Set(ORDER_QUICK_UPDATE_FEATURES);
const isOrderExcelStyleViewOnlyFeature = (feature) =>
  ORDER_EXCEL_EXPORT_ONLY_FEATURES.has(feature) || ORDER_QUICK_UPDATE_ONLY_FEATURES.has(feature);

/** Phạm vi truy cập dữ liệu (users.data_access_scope). */
const DATA_ACCESS_SCOPE_OPTIONS = [
  { value: "PERSONAL", label: "Cá nhân" },
  { value: "DEPARTMENT", label: "Bộ phận" },
  { value: "FULL", label: "Đầy đủ" },
];

const normalizeDataAccessScope = (value) => {
  const v = String(value || "PERSONAL").trim().toUpperCase();
  if (v === "FULL" || v === "DEPARTMENT" || v === "PERSONAL") return v;
  return "PERSONAL";
};

const effectiveDataAccessScope = (user) => {
  if (user?.isAdmin === true) return "FULL";
  return normalizeDataAccessScope(user?.dataAccessScope);
};

/** Nhãn nhóm con trong bảng phân quyền — khớp menu «Sản phẩm > Danh sách sản phẩm» (không dùng «Sản phẩm» trùng tên nhóm). */
const PRODUCT_UI_PARENT_LABEL = "Sản phẩm";

/** Tiêu đề dòng nhóm cha trong bảng (hiển thị thân thiện hơn mã nội bộ SẢN PHẨM). */
const PERMISSION_GROUP_HEADER_DISPLAY = {
  "SẢN PHẨM": "Sản phẩm",
  "NGƯỜI DÙNG": "Hệ thống",
  "CHĂM SÓC KHÁCH HÀNG": "Chăm sóc khách hàng",
};

function formatPermissionGroupHeader(parentKey) {
  return PERMISSION_GROUP_HEADER_DISPLAY[parentKey] ?? parentKey;
}

/** Hiển thị dưới «Nhà cung cấp», cùng khóa JSON «SẢN PHẨM / Đánh giá - bình luận». */
const PROVIDER_RATING_DISPLAY_LABEL = "Bình luận - Đánh giá";

const PRODUCTS_PERSONAL_FEATURE = "SẢN PHẨM / Sản phẩm cá nhân";
const PRODUCTS_SHARED_FEATURE = "SẢN PHẨM / Sản phẩm dùng chung";
const PRODUCTS_EXPORT_FEATURE = "SẢN PHẨM / Xuất file excel";

const PRODUCT_FULL_CHILD_ORDER = [PRODUCTS_PERSONAL_FEATURE, PRODUCTS_SHARED_FEATURE];

/** Luôn hiển thị đủ cột Xem / Thêm / Sửa / Xóa (kể cả JSON quyền cũ chưa có khóa create|edit|delete). */
const PRODUCT_PERSONAL_SHARED_FORCE_FULL_COLUMNS = new Set([
  PRODUCTS_PERSONAL_FEATURE,
  PRODUCTS_SHARED_FEATURE,
]);

/** Xuất Excel + Đánh giá/bình luận NCC + Thông báo: chỉ một checkbox đầu dòng (lưu qua cờ view), không có cột Xem/Thêm/Sửa/Xóa. */
const PRODUCT_VIEW_FLAG_ONLY_FEATURES = new Set([
  PRODUCTS_EXPORT_FEATURE,
  PROVIDER_RATING_COMMENT_DIVISION_FEATURE,
  NOTIFY_NCC_RATING_REMINDER_FEATURE,
  NOTIFY_TRANSFER_REQUEST_INBOX_FEATURE,
  NOTIFY_TRANSFER_REQUEST_PROCESS_FEATURE,
  ...CSKH_DIVISION_FEATURES,
]);

const CSKH_LEADING_ONLY = new Set(CSKH_DIVISION_FEATURES);

/** Chỉ đổi nhãn hiển thị; khóa `feature` giữ nguyên (conversations, training_ai, …). */
const CSKH_DISPLAY_LABELS = {
  [`${CSKH_DIVISION_PARENT} / conversations`]: "Hội thoại",
  [`${CSKH_DIVISION_PARENT} / training_ai`]: "Đào tạo AI",
  [`${CSKH_DIVISION_PARENT} / multi_channel`]: "Quản lý đa kênh",
  [`${CSKH_DIVISION_PARENT} / setup_ai_staff`]: "Cài đặt nhân sự AI",
};

const THONG_BAO_ROW_SYNTHETIC_RATING = "thong-bao-cho-phep-danh-gia-ncc";
/** Chỉ đổi nhãn hiển thị; khóa `feature` giữ nguyên để khớp JSON đã lưu. */
const THONG_BAO_DISPLAY_LABELS = {
  [NOTIFY_NCC_RATING_REMINDER_FEATURE]: "Nhận thông báo đánh giá NCC",
  [NOTIFY_TRANSFER_REQUEST_INBOX_FEATURE]: "Nhận yêu cầu chuyển đơn",
  [NOTIFY_TRANSFER_REQUEST_PROCESS_FEATURE]: "Thực hiện yêu cầu chuyển đơn",
};
const THONG_BAO_HINTS = {
  [NOTIFY_NCC_RATING_REMINDER_FEATURE]: "Cho phép hiển thị thông báo liên quan đánh giá nhà cung cấp.",
  [NOTIFY_TRANSFER_REQUEST_INBOX_FEATURE]: "Cho phép hiển thị danh sách yêu cầu chuyển đơn từ khách hàng.",
  [NOTIFY_TRANSFER_REQUEST_PROCESS_FEATURE]: "Cho phép thực hiện yêu cầu chuyển đơn của khách hàng.",
};
const THONG_BAO_LEADING_ONLY = new Set([
  NOTIFY_NCC_RATING_REMINDER_FEATURE,
  NOTIFY_TRANSFER_REQUEST_INBOX_FEATURE,
  NOTIFY_TRANSFER_REQUEST_PROCESS_FEATURE,
]);

/** Nút lọc Danh sách khách hàng — chỉ cờ view. */
const CUSTOMER_FILTER_VIEW_ONLY_FEATURES = new Set(CUSTOMER_FILTER_DIVISION_FEATURES);

/** JSON phòng ban cũ — map sang mục mới khi mở màn. */
const PRODUCTS_LEGACY_MAIN = "SẢN PHẨM / Sản phẩm";
const PRODUCTS_LEGACY_ADD_PERSONAL = "SẢN PHẨM / Thêm sản phẩm cá nhân";
const PRODUCTS_LEGACY_ADD_SHARED = "SẢN PHẨM / Thêm sản phẩm dùng chung";

/** Tránh hiển thị «Sản phẩm > Sản phẩm»: legacy và nhóm SP dùng chung tên hiển thị «Danh sách sản phẩm». */
function divisionFeatureRowLabel(featureKey, pathSegmentLabel) {
  if (featureKey === PRODUCTS_LEGACY_MAIN) return PRODUCT_UI_PARENT_LABEL;
  return pathSegmentLabel;
}

/** Thứ tự cố định trong nhóm SẢN PHẨM: Danh sách SP → Danh mục → NCC → … */
function sanPhamPermissionRowSortKey(item) {
  if (item.kind === "product-root") return 0;
  if (item.kind === "leaf" && item.feature === CATEGORIES_DIVISION_FEATURE) return 1;
  if (item.kind === "provider-root") return 2;
  return 4;
}

/** Đồng bộ hàng ĐƠN HÀNG (legacy) khi lưu — view = bất kỳ quyền xem con; CRUD từ Đơn thường/VAT (vận chuyển + mua hộ). */
function deriveOrderAggregatePermission(getPerm) {
  const n = getPerm(ORDERS_NORMAL_DIVISION_FEATURE);
  const v = getPerm(ORDERS_VAT_DIVISION_FEATURE);
  const mn = getPerm(ORDERS_MUA_HO_NORMAL_DIVISION_FEATURE);
  const mv = getPerm(ORDERS_MUA_HO_VAT_DIVISION_FEATURE);
  const view =
    !!n?.view ||
    !!v?.view ||
    !!mn?.view ||
    !!mv?.view ||
    !!getPerm(ORDERS_MUA_HO_DIVISION_FEATURE)?.view ||
    ORDER_EXCEL_EXPORT_FEATURES.some((f) => !!getPerm(f)?.view) ||
    ORDER_QUICK_UPDATE_FEATURES.some((f) => !!getPerm(f)?.view) ||
    ORDER_RECONCILE_COLUMN_FEATURES.some((f) => !!getPerm(f)?.view);
  return {
    view,
    create: !!(n?.create || v?.create || mn?.create || mv?.create),
    edit: !!(n?.edit || v?.edit || mn?.edit || mv?.edit),
    delete: !!(n?.delete || v?.delete || mn?.delete || mv?.delete),
  };
}

function orderFeatureLeafLabel(featureKey, pathAfterParent) {
  const segments = String(featureKey || "")
    .split(" / ")
    .slice(1)
    .map((s) => s.trim())
    .filter(Boolean);
  if (segments.length > 1) return segments[segments.length - 1];
  return pathAfterParent;
}

function accountingLeafLabel(featureKey) {
  const parts = String(featureKey || "")
    .split(" / ")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts[parts.length - 1] || featureKey;
}

/** Folder theo menu/tab thực tế dưới nhóm KẾ TOÁN. */
function buildKeToanChildItems(rawChildren) {
  const byFeature = new Map(rawChildren.map((c) => [c.feature, c]));
  const folders = [
    { id: "ke-toan-bao-cao", title: "Báo cáo", leaves: ACCOUNTING_REPORT_TAB_LEAVES },
    { id: "ke-toan-cong-no", title: "Công nợ", leaves: ACCOUNTING_DEBT_LEAVES },
    { id: "ke-toan-phieu", title: "Phiếu thu/chi", leaves: ACCOUNTING_VOUCHER_LEAVES },
    { id: "ke-toan-so-chi", title: "Sổ chi", leaves: ACCOUNTING_SO_CHI_LEAVES },
    { id: "ke-toan-cau-hinh", title: "Cấu hình", leaves: ACCOUNTING_CONFIG_LEAVES },
    { id: "ke-toan-tien-ich", title: "Tiện ích", leaves: ACCOUNTING_UTILITY_LEAVES },
  ];
  const modules = [];
  for (const folder of folders) {
    const inner = folder.leaves
      .map((f) => {
        const c = byFeature.get(f);
        if (!c) return null;
        return { kind: "leaf", feature: f, label: accountingLeafLabel(f) };
      })
      .filter(Boolean);
    if (inner.length > 0) {
      modules.push({
        kind: "order-root",
        id: folder.id,
        title: folder.title,
        inner,
      });
    }
  }
  return modules;
}

function buildOrderHangChildItems(rawChildren) {
  const byFeature = new Map(rawChildren.map((c) => [c.feature, c]));
  const consumed = new Set([
    ORDER_DIVISION_FEATURE,
    ORDERS_NORMAL_DIVISION_FEATURE,
    ORDERS_VAT_DIVISION_FEATURE,
    ORDERS_MUA_HO_DIVISION_FEATURE,
    ORDERS_MUA_HO_NORMAL_DIVISION_FEATURE,
    ORDERS_MUA_HO_VAT_DIVISION_FEATURE,
    ...ORDER_EXCEL_EXPORT_FEATURES,
    ...ORDER_QUICK_UPDATE_FEATURES,
    ...ORDER_RECONCILE_COLUMN_FEATURES,
  ]);

  const pushLeaf = (inner, featureKey) => {
    const c = byFeature.get(featureKey);
    if (c) inner.push({ kind: "leaf", feature: c.feature, label: c.label });
  };

  const muahoInner = [];
  pushLeaf(muahoInner, ORDERS_MUA_HO_NORMAL_DIVISION_FEATURE);
  pushLeaf(muahoInner, ORDERS_MUA_HO_VAT_DIVISION_FEATURE);

  const vanChuyenInner = [];
  pushLeaf(vanChuyenInner, ORDERS_NORMAL_DIVISION_FEATURE);
  pushLeaf(vanChuyenInner, ORDERS_VAT_DIVISION_FEATURE);

  const excelLeaves = ORDER_EXCEL_EXPORT_FEATURES.map((f) => byFeature.get(f)).filter(Boolean);
  if (excelLeaves.length > 0) {
    vanChuyenInner.push({
      kind: "order-excel-folder",
      id: "order-excel-exports",
      title: "Xuất excel",
      children: excelLeaves.map((c) => ({ kind: "leaf", feature: c.feature, label: c.label })),
    });
  }

  const columnLeaves = ORDER_RECONCILE_COLUMN_FEATURES.map((f) => byFeature.get(f)).filter(Boolean);

  const quickUpdateLeaves = ORDER_QUICK_UPDATE_FEATURES.map((f) => byFeature.get(f)).filter(Boolean);
  if (quickUpdateLeaves.length > 0) {
    vanChuyenInner.push({
      kind: "order-excel-folder",
      id: "order-quick-update",
      title: "Cập nhật nhanh",
      children: quickUpdateLeaves.map((c) => ({ kind: "leaf", feature: c.feature, label: c.label })),
    });
  }

  for (const c of rawChildren) {
    if (!consumed.has(c.feature)) {
      vanChuyenInner.push({ kind: "leaf", feature: c.feature, label: c.label });
    }
  }

  const modules = [];
  if (muahoInner.length > 0) {
    modules.push({
      kind: "order-root",
      id: "order-muaho",
      title: "Đơn hàng mua hộ",
      inner: muahoInner,
    });
  }
  if (vanChuyenInner.length > 0) {
    modules.push({
      kind: "order-root",
      id: "order-vanchuyen",
      title: "Đơn hàng vận chuyển",
      inner: vanChuyenInner,
    });
  }
  if (columnLeaves.length > 0) {
    modules.push({
      kind: "order-root",
      id: "order-display-columns",
      title: "Cột hiển thị",
      inner: columnLeaves.map((c) => ({ kind: "leaf", feature: c.feature, label: c.label })),
    });
  }

  if (modules.length === 0) {
    const nonColumn = rawChildren.filter((x) => !ORDER_DISPLAY_COLUMN_SET.has(x.feature));
    const items = nonColumn.map((c) => ({ kind: "leaf", feature: c.feature, label: c.label }));
    if (columnLeaves.length > 0) {
      items.push({
        kind: "order-root",
        id: "order-display-columns",
        title: "Cột hiển thị",
        inner: columnLeaves.map((c) => ({ kind: "leaf", feature: c.feature, label: c.label })),
      });
    }
    return items;
  }

  return modules;
}

function buildNguoiDungChildItems(rawChildren) {
  const items = [];
  for (const c of rawChildren) {
    if (c.feature === CUSTOMERS_DIVISION_FEATURE) {
      items.push({
        kind: "customer-root",
        label: c.label,
        mainFeature: CUSTOMERS_DIVISION_FEATURE,
        filterLeaves: CUSTOMER_FILTER_GROUP_NAMES.map((groupName) => ({
          kind: "leaf",
          feature: customerFilterGroupToFeature(groupName),
          label: groupName,
        })),
      });
      continue;
    }
    if (CUSTOMER_FILTER_VIEW_ONLY_FEATURES.has(c.feature)) continue;
    items.push({ kind: "leaf", feature: c.feature, label: c.label });
  }
  return items;
}

function buildSanPhamChildItems(rawChildren) {
  const byFeature = new Map(rawChildren.map((c) => [c.feature, c]));
  const personal = byFeature.get(PRODUCTS_PERSONAL_FEATURE);
  const shared = byFeature.get(PRODUCTS_SHARED_FEATURE);
  const exportLeaf = byFeature.get(PRODUCTS_EXPORT_FEATURE);
  const categoryLeaf = byFeature.get(CATEGORIES_DIVISION_FEATURE);
  const providerLeaf = byFeature.get(PROVIDERS_DIVISION_FEATURE);
  const ratingLeaf = byFeature.get(PROVIDER_RATING_COMMENT_DIVISION_FEATURE);
  const items = [];
  if (personal || shared || exportLeaf) {
    const fullLeaves = PRODUCT_FULL_CHILD_ORDER.map((f) => byFeature.get(f)).filter(Boolean);
    items.push({
      kind: "product-root",
      label: PRODUCT_UI_PARENT_LABEL,
      fullLeaves: fullLeaves.map((c) => ({ feature: c.feature, label: c.label })),
      exportLeaf: exportLeaf ? { feature: exportLeaf.feature, label: exportLeaf.label } : null,
    });
  }
  const extraLeaves = [];
  const hasProductListGroup = !!(personal || shared || exportLeaf);
  for (const c of rawChildren) {
    if (c.feature === PRODUCTS_PERSONAL_FEATURE) continue;
    if (c.feature === PRODUCTS_SHARED_FEATURE) continue;
    if (c.feature === PRODUCTS_EXPORT_FEATURE) continue;
    if (c.feature === CATEGORIES_DIVISION_FEATURE) continue;
    if (c.feature === PROVIDERS_DIVISION_FEATURE) continue;
    if (c.feature === PROVIDER_RATING_COMMENT_DIVISION_FEATURE) continue;
    if (c.feature === PRODUCTS_LEGACY_MAIN && hasProductListGroup) continue;
    extraLeaves.push({
      kind: "leaf",
      feature: c.feature,
      label: divisionFeatureRowLabel(c.feature, c.label),
    });
  }
  /**
   * Sau nhóm «Danh sách sản phẩm»: Danh mục ngay bên dưới (Danh sách SP luôn phía trên Danh mục),
   * rồi Nhà cung cấp → Bình luận → các mục khác.
   */
  const orderedTail = [];
  if (categoryLeaf) {
    orderedTail.push({ kind: "leaf", feature: categoryLeaf.feature, label: categoryLeaf.label });
  }
  if (providerLeaf || ratingLeaf) {
    orderedTail.push({
      kind: "provider-root",
      label: providerLeaf?.label ?? "Nhà cung cấp",
      providerLeaf: providerLeaf
        ? { feature: providerLeaf.feature, label: providerLeaf.label }
        : null,
      ratingLeaf: ratingLeaf
        ? { feature: ratingLeaf.feature, label: PROVIDER_RATING_DISPLAY_LABEL }
        : null,
    });
  }
  orderedTail.push(...extraLeaves);
  items.push(...orderedTail);
  items.sort((a, b) => sanPhamPermissionRowSortKey(a) - sanPhamPermissionRowSortKey(b));
  return items;
}

function buildThongBaoChildItems(rawChildren) {
  const byFeature = new Map(rawChildren.map((c) => [c.feature, c]));
  const nhac = byFeature.get(NOTIFY_NCC_RATING_REMINDER_FEATURE);
  const nhan = byFeature.get(NOTIFY_TRANSFER_REQUEST_INBOX_FEATURE);
  const thucHien = byFeature.get(NOTIFY_TRANSFER_REQUEST_PROCESS_FEATURE);
  const items = [];
  if (nhac) {
    items.push({
      kind: "leaf",
      feature: nhac.feature,
      label: THONG_BAO_DISPLAY_LABELS[NOTIFY_NCC_RATING_REMINDER_FEATURE] ?? nhac.label,
    });
  }
  items.push({
    kind: "leaf",
    feature: PROVIDER_RATING_COMMENT_DIVISION_FEATURE,
    label: "Xử lý đánh giá NCC",
    rowKey: THONG_BAO_ROW_SYNTHETIC_RATING,
  });
  if (nhan) {
    items.push({
      kind: "leaf",
      feature: nhan.feature,
      label: THONG_BAO_DISPLAY_LABELS[NOTIFY_TRANSFER_REQUEST_INBOX_FEATURE] ?? nhan.label,
    });
  }
  if (thucHien) {
    items.push({
      kind: "leaf",
      feature: thucHien.feature,
      label: THONG_BAO_DISPLAY_LABELS[NOTIFY_TRANSFER_REQUEST_PROCESS_FEATURE] ?? thucHien.label,
    });
  }
  return items;
}

/** Không có thao tác xóa phù hợp — không hiển thị checkbox Xóa khi phân quyền bộ phận */
const FEATURES_WITHOUT_DELETE_CHECKBOX = new Set([
  "NGƯỜI DÙNG / Khách hàng",
  "NGƯỜI DÙNG / Danh sách users",
  DEPARTMENTS_DIVISION_FEATURE,
]);

/** Màn Phân quyền: chỉ Xem / Sửa — không cột Thêm. */
const FEATURES_WITHOUT_CREATE_CHECKBOX = new Set([DEPARTMENTS_DIVISION_FEATURE]);

/** Đối soát chỉ Xem + Thêm — không checkbox Sửa / Xóa (trừ các mục được mở riêng bên dưới). */
const RECONCILE_FEATURES_VIEW_CREATE_ONLY = new Set([]);

/** Khách hàng thường: luôn hiển thị Sửa/Xóa trong màn cấu hình quyền bộ phận */
const RECONCILE_NORMAL_FORCE_EDIT_DELETE_COLUMNS = new Set(["ĐỐI SOÁT / Khách hàng thường"]);
const RECONCILE_VAT_FORCE_EDIT_DELETE_COLUMNS = new Set(["ĐỐI SOÁT / Khách hàng VAT"]);
const RECONCILE_PARTNER_NORMAL_NO_DELETE = "ĐỐI SOÁT / Đối tác thường";
const RECONCILE_PARTNER_VAT_NO_DELETE = "ĐỐI SOÁT / Đối tác VAT";

/** Đối tác đối soát: luôn có cột Thêm (chưa có bản ghi trong JSON quyền cũ vẫn hiển thị checkbox) */
const RECONCILE_PARTNER_FEATURES_FORCE_CREATE_COLUMN = new Set([
  "ĐỐI SOÁT / Đối tác thường",
  "ĐỐI SOÁT / Đối tác VAT",
]);
const SETTINGS_NO_CREATE_DELETE_FEATURES = new Set(["MỞ RỘNG / Cài đặt"]);

/**
 * Không có quyền Xem thì không có Thêm/Sửa/Xóa (trừ các chức năng chỉ có cờ view).
 * So khớp id phòng ban / bộ phận từ API (number | string) với state URL / parent.
 */
function sameDivisionOrDeptId(a, b) {
  if (a == null || b == null) return false;
  const na = Number(a);
  const nb = Number(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) return false;
  return na === nb;
}

function stripCrudWithoutView(feature, perm) {
  if (!perm || typeof perm !== "object") return perm;
  if (PRODUCT_VIEW_FLAG_ONLY_FEATURES.has(feature)) return perm;
  if (CUSTOMER_FILTER_VIEW_ONLY_FEATURES.has(feature)) return perm;
  if (isOrderExcelStyleViewOnlyFeature(feature)) return perm;
  if (ORDER_VIEW_ONLY_FEATURES.has(feature) || ACCOUNTING_VIEW_ONLY_FEATURES.has(feature)) {
    return { ...perm, create: false, edit: false, delete: false };
  }
  if (!perm.view) {
    return { ...perm, create: false, edit: false, delete: false };
  }
  return perm;
}

/** Checkbox “chọn tất cả” dòng — hỗ trợ trạng thái indeterminate. */
function RowSelectAllCheckbox({ checked, indeterminate, disabled, onChange, ariaLabel, title }) {
  return (
    <input
      type="checkbox"
      className="form-check-input m-0"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      title={title}
      aria-label={ariaLabel}
      ref={(input) => {
        if (input) input.indeterminate = indeterminate;
      }}
    />
  );
}

/** Checkbox → cách 1ch → tên chức năng. */
function DeptPermNameRow({
  depth = 2,
  expandSlot,
  checkPrimary,
  checkSecondary,
  label,
  labelClassName = "",
  labelTitle,
}) {
  const rowClass =
    depth >= 4 ? "dept-perm-row dept-perm-row--l4" : depth >= 3 ? "dept-perm-row dept-perm-row--l3" : "dept-perm-row";
  const labelBlockCls = ["dept-perm-label-block", labelClassName].filter(Boolean).join(" ");
  return (
    <div className={rowClass}>
      <div className="dept-perm-expand-slot">{expandSlot ?? null}</div>
      <div className="dept-perm-controls-cluster">
        {checkPrimary ?? null}
        {checkSecondary ?? null}
      </div>
      <div className={labelBlockCls}>
        <span className="dept-perm-label-text" title={labelTitle}>
          {label}
        </span>
      </div>
    </div>
  );
}

const DivisionPermissionEditor = forwardRef(function DivisionPermissionEditor(
  {
    departmentId,
    items,
    setItems,
    reloadDepartments,
    selectedDivisionId,
    setSelectedDivisionId,
    requestedDivisionId = NaN,
    panelHeaderTitle,
    hideFooterSave = false,
    /** Khi tạo/sửa bộ phận thuộc phòng ban khác với `departmentId` (màn cây doanh nghiệp). */
    createDivisionDepartmentId,
  },
  ref
) {
  const loadDepartments = reloadDepartments;
  const effectiveDeptIdForCrud = createDivisionDepartmentId ?? departmentId;

  const divisionEditorDepartmentLabel = useMemo(() => {
    const id = effectiveDeptIdForCrud;
    if (id == null) return "";
    const dep = items.find((d) => Number(d.id) === Number(id));
    return (dep?.name || "").trim() || "—";
  }, [items, effectiveDeptIdForCrud]);

  const [showUserPicker, setShowUserPicker] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [permissionDraft, setPermissionDraft] = useState({});
  /** Thu gọn nhóm "Xuất excel" trong Đơn hàng. */
  const [orderExcelExportsExpanded, setOrderExcelExportsExpanded] = useState(false);
  /** Thu gọn nhóm "Cập nhật nhanh" trong Đơn hàng. */
  const [orderQuickUpdateExpanded, setOrderQuickUpdateExpanded] = useState(false);
  /** Thu gọn mục «Đơn hàng mua hộ» / «Đơn hàng vận chuyển». */
  const [orderModuleExpandedById, setOrderModuleExpandedById] = useState({
    "order-muaho": false,
    "order-vanchuyen": false,
    "order-display-columns": false,
  });
  /** Thu gọn các mục con dưới nhóm Danh sách sản phẩm. */
  const [productModuleExpanded, setProductModuleExpanded] = useState(false);
  const [customerModuleExpanded, setCustomerModuleExpanded] = useState(false);
  const [cskhModuleExpanded, setCskhModuleExpanded] = useState(false);
  /** Mở rộng nhóm gốc (WEBSITE, ĐƠN HÀNG, KHO VẬN…): { [tên nhóm]: true } = đang mở; mặc định thu gọn. CSKH dùng cskhModuleExpanded. */
  const [expandedRootGroups, setExpandedRootGroups] = useState({});
  const isRootGroupExpanded = (parent) =>
    parent === CSKH_DIVISION_PARENT ? cskhModuleExpanded : Boolean(expandedRootGroups[parent]);
  const toggleRootGroup = (parent) =>
    setExpandedRootGroups((prev) => ({ ...prev, [parent]: !prev[parent] }));
  /** Thu gọn các mục con dưới nhóm Nhà cung cấp. */
  const [providerModuleExpanded, setProviderModuleExpanded] = useState(false);
  const [showDivisionEditor, setShowDivisionEditor] = useState(false);
  const [editingDivision, setEditingDivision] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  /** User id đã bấm gỡ khỏi bộ phận hiện tại — chỉ ghi DB khi nhấn Lưu */
  const [pendingRemovalIds, setPendingRemovalIds] = useState([]);
  /** Thay đổi phạm vi truy cập chưa lưu: userId → PERSONAL | DEPARTMENT | FULL */
  const [accessScopeEdits, setAccessScopeEdits] = useState({});
  const [canEditDepartmentPage, setCanEditDepartmentPage] = useState(true);
  const [viewerIsAdmin, setViewerIsAdmin] = useState(false);
  const hasAppliedQueryDivisionRef = useRef(false);
  const loadAdminUsers = async () => {
    try {
      const response = await axiosInstance.get("/api/users/searchAdminUsers", {
        params: { page: 0, size: 500 },
      });
      setAdminUsers(response?.data?.page?.content || []);
    } catch (error) {
      setAdminUsers([]);
    }
  };

  useEffect(() => {
    loadAdminUsers();
  }, []);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/api/users/myFlags")
      .then((res) => {
        if (!cancelled) setViewerIsAdmin(res?.data?.data?.isAdmin === true);
      })
      .catch(() => {
        if (!cancelled) setViewerIsAdmin(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchDepartmentMenuPermissions(axiosInstance);
      if (!cancelled) setCanEditDepartmentPage(Boolean(p?.canEdit));
    })();
    return () => {
      cancelled = true;
    };
  }, []);


  const department = useMemo(
    () => items.find((item) => sameDivisionOrDeptId(item.id, departmentId)),
    [items, departmentId]
  );
  const divisions = useMemo(() => department?.divisions || [], [department]);
  const activeDivision =
    divisions.find((div) => sameDivisionOrDeptId(div.id, selectedDivisionId)) || divisions[0] || null;

  const findActiveDivisionPermissionRow = (feature) => {
    const list = activeDivision?.permissions || [];
    let found = list.find((p) => p.feature === feature);
    if (!found && feature === DEPARTMENTS_DIVISION_FEATURE) {
      found = list.find((p) => p.feature === DEPARTMENTS_DIVISION_FEATURE_LEGACY);
    }
    if (!found) {
      for (const [oldKey, newKey] of Object.entries(ORDER_QUICK_UPDATE_FEATURE_KEY_MIGRATION)) {
        if (newKey === feature) {
          found = list.find((p) => p.feature === oldKey);
          if (found) break;
        }
      }
    }
    return found;
  };

  useEffect(() => {
    setPendingRemovalIds([]);
    setAccessScopeEdits({});
  }, [activeDivision?.id]);

  const buildDivisionUserUpdatePayload = (user, overrides = {}) => {
    const divIds =
      overrides.divisionIds !== undefined
        ? overrides.divisionIds
        : userDivisionIdList(user);
    return {
      id: user.id,
      username: user.username || null,
      email: user.email || null,
      name: user.name || null,
      phoneNumber: user.phoneNumber || null,
      officeAddress: user.officeAddress || null,
      divisionIds: divIds,
      divisionId: overrides.divisionId !== undefined ? overrides.divisionId : divIds.length ? divIds[0] : null,
      isAdmin: user.isAdmin === true,
      isMember: user.isMember !== false,
      dataAccessScope: effectiveDataAccessScope({
        ...user,
        isAdmin: user.isAdmin === true,
        dataAccessScope:
          overrides.dataAccessScope !== undefined
            ? overrides.dataAccessScope
            : accessScopeEdits[user.id] ?? user.dataAccessScope,
      }),
    };
  };

  const getUserAccessScope = (user) => {
    if (user?.isAdmin === true) return "FULL";
    return normalizeDataAccessScope(accessScopeEdits[user.id] ?? user.dataAccessScope);
  };

  useEffect(() => {
    if (!items?.length) return;
    if (!department) return;
    if (!divisions.length) {
      hasAppliedQueryDivisionRef.current = false;
      setSelectedDivisionId(null);
      return;
    }
    const req = Number(requestedDivisionId);
    const hasValidUrlDivision =
      Number.isFinite(req) &&
      !Number.isNaN(req) &&
      divisions.some((d) => sameDivisionOrDeptId(d.id, req));
    if (!hasAppliedQueryDivisionRef.current && hasValidUrlDivision) {
      setSelectedDivisionId(req);
      hasAppliedQueryDivisionRef.current = true;
      return;
    }
    if (
      selectedDivisionId != null &&
      selectedDivisionId !== "" &&
      !divisions.some((d) => sameDivisionOrDeptId(d.id, selectedDivisionId))
    ) {
      setSelectedDivisionId(Number(divisions[0].id));
    }
  }, [
    items,
    department,
    divisions,
    requestedDivisionId,
    selectedDivisionId,
    setSelectedDivisionId,
  ]);

  useEffect(() => {
    const next = {};
    (activeDivision?.permissions || []).forEach((p) => {
      const featureKeyRaw =
        p.feature === DEPARTMENTS_DIVISION_FEATURE_LEGACY ? DEPARTMENTS_DIVISION_FEATURE : p.feature;
      const featureKey = migrateDivisionQuickUpdateFeatureKey(featureKeyRaw);
      const denyDelete = FEATURES_WITHOUT_DELETE_CHECKBOX.has(featureKey);
      const denyCreate = FEATURES_WITHOUT_CREATE_CHECKBOX.has(featureKey);
      const reconcileVCOnly = RECONCILE_FEATURES_VIEW_CREATE_ONLY.has(featureKey);
      const viewValue = !!p.view;
      next[featureKey] = {
        view: viewValue,
        create: denyCreate ? false : !!p.create,
        edit: reconcileVCOnly ? false : !!p.edit,
        delete: reconcileVCOnly || denyDelete ? false : !!p.delete,
      };
    });

    const pick = (k) => next[k];
    const legacyMain = pick(PRODUCTS_LEGACY_MAIN);
    const legacyPer = pick(PRODUCTS_LEGACY_ADD_PERSONAL);
    const legacyShr = pick(PRODUCTS_LEGACY_ADD_SHARED);

    if (!pick(PRODUCTS_PERSONAL_FEATURE) && (legacyMain || legacyPer)) {
      next[PRODUCTS_PERSONAL_FEATURE] = {
        view: !!(legacyMain?.view),
        create: !!(legacyPer?.view ?? legacyMain?.create),
        edit: !!(legacyMain?.edit),
        delete: !!(legacyMain?.delete),
      };
    }
    if (!pick(PRODUCTS_SHARED_FEATURE) && (legacyMain || legacyShr)) {
      next[PRODUCTS_SHARED_FEATURE] = {
        view: !!(legacyMain?.view),
        create: !!(legacyShr?.view ?? legacyMain?.create),
        edit: !!(legacyMain?.edit),
        delete: !!(legacyMain?.delete),
      };
    }

    const legacyOrder = pick(ORDER_DIVISION_FEATURE);
    if (!pick(ORDERS_NORMAL_DIVISION_FEATURE) && legacyOrder) {
      next[ORDERS_NORMAL_DIVISION_FEATURE] = {
        view: !!legacyOrder.view,
        create: !!legacyOrder.create,
        edit: !!legacyOrder.edit,
        delete: !!legacyOrder.delete,
      };
    }
    if (!pick(ORDERS_VAT_DIVISION_FEATURE) && legacyOrder) {
      next[ORDERS_VAT_DIVISION_FEATURE] = {
        view: !!legacyOrder.view,
        create: !!legacyOrder.create,
        edit: !!legacyOrder.edit,
        delete: !!legacyOrder.delete,
      };
    }

    const legacyMuaHo = pick(ORDERS_MUA_HO_DIVISION_FEATURE);
    if (legacyMuaHo?.view) {
      if (!pick(ORDERS_MUA_HO_NORMAL_DIVISION_FEATURE)) {
        next[ORDERS_MUA_HO_NORMAL_DIVISION_FEATURE] = {
          view: true,
          create: false,
          edit: false,
          delete: false,
        };
      }
      if (!pick(ORDERS_MUA_HO_VAT_DIVISION_FEATURE)) {
        next[ORDERS_MUA_HO_VAT_DIVISION_FEATURE] = {
          view: true,
          create: false,
          edit: false,
          delete: false,
        };
      }
    }

    // Map quyền KẾ TOÁN cũ (nhóm) → leaf theo tab khi chưa có leaf trong JSON.
    for (const [legacyKey, leaves] of Object.entries(ACCOUNTING_LEGACY_TO_LEAVES)) {
      const legacyPerm = pick(legacyKey);
      if (!legacyPerm) continue;
      if (!legacyPerm.view && !legacyPerm.create && !legacyPerm.edit && !legacyPerm.delete) {
        continue;
      }
      for (const leaf of leaves) {
        if (pick(leaf)) continue;
        next[leaf] = {
          view: !!legacyPerm.view,
          create: !!legacyPerm.create,
          edit: !!legacyPerm.edit,
          delete: !!legacyPerm.delete,
        };
      }
    }

    const anyCustomerFilterView = CUSTOMER_FILTER_DIVISION_FEATURES.some((f) => !!next[f]?.view);
    if (anyCustomerFilterView) {
      const parent = next[CUSTOMERS_DIVISION_FEATURE] || {
        view: false,
        create: false,
        edit: false,
        delete: false,
      };
      next[CUSTOMERS_DIVISION_FEATURE] = stripCrudWithoutView(CUSTOMERS_DIVISION_FEATURE, {
        ...parent,
        view: true,
      });
    }

    if (next.CHAT?.view && !CSKH_DIVISION_FEATURES.some((f) => next[f] != null)) {
      for (const f of CSKH_DIVISION_FEATURES) {
        next[f] = { view: true, create: false, edit: false, delete: false };
      }
    }

    for (const feat of Object.keys(next)) {
      next[feat] = stripCrudWithoutView(feat, next[feat]);
    }

    setPermissionDraft(next);
  }, [activeDivision]);

  const featureTree = useMemo(() => {
    const map = new Map();
    allFeatures.forEach((feature) => {
      const parts = feature.split(" / ").map((s) => s.trim());
      if (parts.length >= 3 && parts[0] === "NGƯỜI DÙNG" && parts[1] === "Khách hàng") {
        return;
      }
      const parent = parts[0] || "Khác";
      const child = parts[1] || null;
      if (!map.has(parent)) map.set(parent, []);
      if (child) {
        const label =
          parent === "ĐƠN HÀNG"
            ? orderFeatureLeafLabel(feature, child)
            : parent === "KẾ TOÁN"
              ? accountingLeafLabel(feature)
              : parent === CSKH_DIVISION_PARENT
                ? CSKH_DISPLAY_LABELS[feature] ?? child
                : divisionFeatureRowLabel(feature, child);
        map.get(parent).push({ feature, label });
      } else {
        map.get(parent).push({ feature, label: parent });
      }
    });
    return Array.from(map.entries()).map(([parent, children]) => ({
      parent,
      children:
        parent === "ĐƠN HÀNG"
          ? buildOrderHangChildItems(children)
          : parent === "KẾ TOÁN"
            ? buildKeToanChildItems(children)
            : parent === "SẢN PHẨM"
              ? buildSanPhamChildItems(children)
              : parent === "THÔNG BÁO"
                ? buildThongBaoChildItems(children)
                : parent === "NGƯỜI DÙNG"
                  ? buildNguoiDungChildItems(children)
                  : children.map((c) => ({ kind: "leaf", feature: c.feature, label: c.label })),
    }));
  }, []);

  const getPermission = (feature) => {
    const fromDraft = permissionDraft[feature];
    const found = findActiveDivisionPermissionRow(feature);
    const raw =
      fromDraft ||
      (found
        ? {
            view: !!found.view,
            create: !!found.create,
            edit: !!found.edit,
            delete: !!found.delete,
          }
        : { view: false, create: false, edit: false, delete: false });
    return stripCrudWithoutView(feature, raw);
  };

  const supportsDeleteAction = (feature) => {
    if (feature === PARTNERS_DIVISION_FEATURE) return true;
    if (PRODUCT_PERSONAL_SHARED_FORCE_FULL_COLUMNS.has(feature)) return true;
    if (ORDER_FULL_CRUD_CHILD_FEATURES.has(feature)) return true;
    if (ACCOUNTING_FULL_CRUD_FEATURES.has(feature)) return true;
    if (PRODUCT_VIEW_FLAG_ONLY_FEATURES.has(feature)) return false;
    if (CUSTOMER_FILTER_VIEW_ONLY_FEATURES.has(feature)) return false;
    if (isOrderExcelStyleViewOnlyFeature(feature)) return false;
    if (ORDER_VIEW_ONLY_FEATURES.has(feature) || ACCOUNTING_VIEW_ONLY_FEATURES.has(feature)) return false;
    if (RECONCILE_VAT_FORCE_EDIT_DELETE_COLUMNS.has(feature)) return true;
    if (feature === RECONCILE_PARTNER_NORMAL_NO_DELETE) return false;
    if (feature === RECONCILE_PARTNER_VAT_NO_DELETE) return false;
    if (RECONCILE_NORMAL_FORCE_EDIT_DELETE_COLUMNS.has(feature)) return true;
    if (RECONCILE_FEATURES_VIEW_CREATE_ONLY.has(feature)) return false;
    if (FEATURES_WITHOUT_DELETE_CHECKBOX.has(feature)) return false;
    if (SETTINGS_NO_CREATE_DELETE_FEATURES.has(feature)) return false;
    const found = findActiveDivisionPermissionRow(feature);
    return found?.delete !== undefined && found?.delete !== null;
  };

  const supportsEditAction = (feature) => {
    if (PRODUCT_PERSONAL_SHARED_FORCE_FULL_COLUMNS.has(feature)) return true;
    if (ORDER_FULL_CRUD_CHILD_FEATURES.has(feature)) return true;
    if (ACCOUNTING_FULL_CRUD_FEATURES.has(feature)) return true;
    if (PRODUCT_VIEW_FLAG_ONLY_FEATURES.has(feature)) return false;
    if (CUSTOMER_FILTER_VIEW_ONLY_FEATURES.has(feature)) return false;
    if (isOrderExcelStyleViewOnlyFeature(feature)) return false;
    if (ORDER_VIEW_ONLY_FEATURES.has(feature) || ACCOUNTING_VIEW_ONLY_FEATURES.has(feature)) return false;
    if (RECONCILE_VAT_FORCE_EDIT_DELETE_COLUMNS.has(feature)) return true;
    if (RECONCILE_NORMAL_FORCE_EDIT_DELETE_COLUMNS.has(feature)) return true;
    if (RECONCILE_FEATURES_VIEW_CREATE_ONLY.has(feature)) return false;
    const found = findActiveDivisionPermissionRow(feature);
    return found?.edit !== undefined && found?.edit !== null;
  };

  const userDivisionIdList = (u) => {
    if (Array.isArray(u.divisionIds) && u.divisionIds.length) {
      return u.divisionIds.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
    }
    if (u.divisionId != null && u.divisionId !== "") return [Number(u.divisionId)];
    return [];
  };

  const supportsCreateAction = (feature) => {
    if (PRODUCT_PERSONAL_SHARED_FORCE_FULL_COLUMNS.has(feature)) return true;
    if (ORDER_FULL_CRUD_CHILD_FEATURES.has(feature)) return true;
    if (ACCOUNTING_FULL_CRUD_FEATURES.has(feature)) return true;
    if (PRODUCT_VIEW_FLAG_ONLY_FEATURES.has(feature)) return false;
    if (CUSTOMER_FILTER_VIEW_ONLY_FEATURES.has(feature)) return false;
    if (isOrderExcelStyleViewOnlyFeature(feature)) return false;
    if (ORDER_VIEW_ONLY_FEATURES.has(feature) || ACCOUNTING_VIEW_ONLY_FEATURES.has(feature)) return false;
    if (SETTINGS_NO_CREATE_DELETE_FEATURES.has(feature)) return false;
    if (FEATURES_WITHOUT_CREATE_CHECKBOX.has(feature)) return false;
    if (RECONCILE_PARTNER_FEATURES_FORCE_CREATE_COLUMN.has(feature)) return true;
    const found = findActiveDivisionPermissionRow(feature);
    return found?.create !== undefined && found?.create !== null;
  };

  const activeDivisionUsers = useMemo(() => {
    if (!activeDivision) return [];
    const aid = Number(activeDivision.id);
    return adminUsers.filter((u) => userDivisionIdList(u).includes(aid));
  }, [adminUsers, activeDivision]);

  const visibleDivisionUsers = useMemo(() => {
    const drop = new Set(pendingRemovalIds.map((x) => Number(x)));
    return activeDivisionUsers.filter((u) => !drop.has(Number(u.id)));
  }, [activeDivisionUsers, pendingRemovalIds]);

  const queueRemoveUserFromDivision = (userId) => {
    if (!canEditDepartmentPage) return;
    const id = Number(userId);
    if (Number.isNaN(id)) return;
    setPendingRemovalIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  /** Chỉ member nội bộ có thể gán vào bộ phận — không hiển thị administrator. */
  const selectableUsers = useMemo(() => {
    if (!activeDivision) return [];
    const aid = Number(activeDivision.id);
    return adminUsers.filter((u) => {
      if (u.isAdmin === true) return false;
      if (u.isMember !== true) return false;
      return !userDivisionIdList(u).includes(aid);
    });
  }, [adminUsers, activeDivision]);

  const onTogglePermission = (feature, key, checked) => {
    if (!canEditDepartmentPage) return;
    if (PRODUCT_VIEW_FLAG_ONLY_FEATURES.has(feature) && key !== "view") return;
    if (CUSTOMER_FILTER_VIEW_ONLY_FEATURES.has(feature) && key !== "view") return;
    if (isOrderExcelStyleViewOnlyFeature(feature) && key !== "view") return;
    if ((ORDER_VIEW_ONLY_FEATURES.has(feature) || ACCOUNTING_VIEW_ONLY_FEATURES.has(feature)) && key !== "view") return;
    if (key === "create" && !supportsCreateAction(feature)) return;
    if (key === "edit" && !supportsEditAction(feature)) return;
    if (key === "delete" && !supportsDeleteAction(feature)) return;
    setPermissionDraft((prev) => {
      const baseFromDivision = () => {
        const x = findActiveDivisionPermissionRow(feature);
        return x
          ? {
              view: !!x.view,
              create: !!x.create,
              edit: !!x.edit,
              delete: !!x.delete,
            }
          : { view: false, create: false, edit: false, delete: false };
      };
      const currentRaw = prev[feature] || baseFromDivision();
      const current = stripCrudWithoutView(feature, currentRaw);
      if (key !== "view" && checked && !current.view) {
        return prev;
      }
      let updated = { ...current, [key]: checked };
      if (key === "view" && !checked) {
        updated = { ...updated, create: false, edit: false, delete: false };
      }
      updated = stripCrudWithoutView(feature, updated);
      const next = { ...prev, [feature]: updated };

      if (CUSTOMER_FILTER_VIEW_ONLY_FEATURES.has(feature) && key === "view" && checked) {
        const parentFeat = CUSTOMERS_DIVISION_FEATURE;
        const parentFromDivision = () => {
          const x = findActiveDivisionPermissionRow(parentFeat);
          return x
            ? {
                view: !!x.view,
                create: !!x.create,
                edit: !!x.edit,
                delete: !!x.delete,
              }
            : { view: false, create: false, edit: false, delete: false };
        };
        const parentRaw = next[parentFeat] || parentFromDivision();
        next[parentFeat] = stripCrudWithoutView(parentFeat, { ...parentRaw, view: true });
      }

      return next;
    });
  };

  const isViewOnlyOrderFeature = (feature) =>
    ORDER_VIEW_ONLY_FEATURES.has(feature) || ACCOUNTING_VIEW_ONLY_FEATURES.has(feature);

  /** Cột quyền hiển thị checkbox (khớp logic render từng ô). */
  const canShowPermissionCell = (feature, key) => {
    if (PRODUCT_VIEW_FLAG_ONLY_FEATURES.has(feature)) return false;
    if (CUSTOMER_FILTER_VIEW_ONLY_FEATURES.has(feature)) return false;
    if (isOrderExcelStyleViewOnlyFeature(feature)) return false;
    if (ORDER_LEVEL3_FEATURES.has(feature)) return false;
    if (key === "view") return true;
    if (isViewOnlyOrderFeature(feature)) return false;
    if (key === "create" && !supportsCreateAction(feature)) return false;
    if (key === "edit" && !supportsEditAction(feature)) return false;
    if (key === "delete" && !supportsDeleteAction(feature)) return false;
    return true;
  };

  /** Các quyền có checkbox trên cột Xem/Thêm/Sửa/Xóa — dùng cho “chọn tất cả”. */
  const getApplicableKeysForRow = (feature) => {
    return ["view", "create", "edit", "delete"].filter((k) => canShowPermissionCell(feature, k));
  };

  const getRowAllSlots = (feature) => {
    const keys = getApplicableKeysForRow(feature);
    return keys.map((k) => ({ feature, key: k }));
  };

  const hasAnyColumnPermissionCheckbox = (feature) => getApplicableKeysForRow(feature).length > 0;

  const isRowAllSelected = (feature) => {
    const slots = getRowAllSlots(feature);
    if (!slots.length) return false;
    return slots.every(({ feature: f, key }) => !!getPermission(f)[key]);
  };

  const isRowSomeSelected = (feature) => {
    const slots = getRowAllSlots(feature);
    const n = slots.filter(({ feature: f, key }) => !!getPermission(f)[key]).length;
    return n > 0 && n < slots.length;
  };

  const onToggleRowAll = (feature, checked) => {
    if (!canEditDepartmentPage) return;
    if (getApplicableKeysForRow(feature).length === 0) return;
    setPermissionDraft((prev) => {
      const base = prev[feature] || getPermission(feature);
      let next = { ...base };
      if (isViewOnlyOrderFeature(feature)) {
        next = { ...next, view: checked, create: false, edit: false, delete: false };
      } else {
        next.view = checked;
        next.create = supportsCreateAction(feature) ? checked : false;
        next.edit = supportsEditAction(feature) ? checked : false;
        next.delete = supportsDeleteAction(feature) ? checked : false;
      }
      return { ...prev, [feature]: stripCrudWithoutView(feature, next) };
    });
  };

  const orderExcelFolderFeatures = (folder) => folder.children.map((c) => c.feature);

  const isOrderExcelFolderAllSelected = (folder) => {
    const feats = orderExcelFolderFeatures(folder);
    if (!feats.length) return false;
    return feats.every((f) => !!getPermission(f).view);
  };

  const isOrderExcelFolderSomeSelected = (folder) => {
    const feats = orderExcelFolderFeatures(folder);
    const n = feats.filter((f) => !!getPermission(f).view).length;
    return n > 0 && n < feats.length;
  };

  const onToggleOrderExcelFolderAll = (folder, checked) => {
    if (!canEditDepartmentPage) return;
    const feats = orderExcelFolderFeatures(folder);
    setPermissionDraft((prev) => {
      const next = { ...prev };
      for (const f of feats) {
        const base = next[f] || getPermission(f);
        next[f] = { ...base, view: checked, create: false, edit: false, delete: false };
      }
      return next;
    });
  };

  const collectOrderModuleSlots = (item) => {
    const slots = [];
    for (const sub of item.inner) {
      if (sub.kind === "leaf") {
        for (const k of getApplicableKeysForRow(sub.feature)) {
          slots.push({ feature: sub.feature, key: k });
        }
      } else if (sub.kind === "order-excel-folder") {
        for (const leaf of sub.children) {
          slots.push({ feature: leaf.feature, key: "view" });
        }
      }
    }
    return slots;
  };

  const isOrderModuleAllSelected = (item) => {
    const slots = collectOrderModuleSlots(item);
    if (!slots.length) return false;
    return slots.every(({ feature, key }) => !!getPermission(feature)[key]);
  };

  const isOrderModuleSomeSelected = (item) => {
    const slots = collectOrderModuleSlots(item);
    const n = slots.filter(({ feature, key }) => !!getPermission(feature)[key]).length;
    return n > 0 && n < slots.length;
  };

  const onToggleOrderModuleAll = (item, checked) => {
    if (!canEditDepartmentPage) return;
    setPermissionDraft((prev) => {
      const next = { ...prev };
      const baseOf = (f) => {
        if (next[f]) return { ...next[f] };
        const x = findActiveDivisionPermissionRow(f);
        return x
          ? {
              view: !!x.view,
              create: !!x.create,
              edit: !!x.edit,
              delete: !!x.delete,
            }
          : { view: false, create: false, edit: false, delete: false };
      };
      for (const sub of item.inner) {
        if (sub.kind === "leaf") {
          const f = sub.feature;
          if (ORDER_FULL_CRUD_CHILD_FEATURES.has(f)) {
            next[f] = {
              view: checked,
              create: checked,
              edit: checked,
              delete: checked,
            };
          } else {
            const base = baseOf(f);
            next[f] = {
              ...base,
              view: checked,
              create: supportsCreateAction(f) ? checked : false,
              edit: supportsEditAction(f) ? checked : false,
              delete: supportsDeleteAction(f) ? checked : false,
            };
          }
        } else if (sub.kind === "order-excel-folder") {
          for (const leaf of sub.children) {
            const f = leaf.feature;
            next[f] = { ...baseOf(f), view: checked, create: false, edit: false, delete: false };
          }
        }
      }
      const getMerged = (f) => next[f] ?? baseOf(f);
      next[ORDER_DIVISION_FEATURE] = deriveOrderAggregatePermission(getMerged);
      return next;
    });
  };

  const collectProductParentSlots = (item) => {
    const slots = [];
    for (const leaf of item.fullLeaves) {
      for (const k of getApplicableKeysForRow(leaf.feature)) {
        slots.push({ feature: leaf.feature, key: k });
      }
    }
    if (item.exportLeaf) slots.push({ feature: item.exportLeaf.feature, key: "view" });
    return slots;
  };

  const isProductParentAllSelected = (item) => {
    const slots = collectProductParentSlots(item);
    if (!slots.length) return false;
    return slots.every(({ feature, key }) => !!getPermission(feature)[key]);
  };

  const isProductParentSomeSelected = (item) => {
    const slots = collectProductParentSlots(item);
    const n = slots.filter(({ feature, key }) => !!getPermission(feature)[key]).length;
    return n > 0 && n < slots.length;
  };

  const onToggleProductParentAll = (item, checked) => {
    if (!canEditDepartmentPage) return;
    setPermissionDraft((prev) => {
      const next = { ...prev };
      const baseOf = (f) => {
        if (next[f]) return { ...next[f] };
        const x = findActiveDivisionPermissionRow(f);
        return x
          ? {
              view: !!x.view,
              create: !!x.create,
              edit: !!x.edit,
              delete: !!x.delete,
            }
          : { view: false, create: false, edit: false, delete: false };
      };
      for (const leaf of item.fullLeaves) {
        const f = leaf.feature;
        if (ORDER_VIEW_ONLY_FEATURES.has(f) || ACCOUNTING_VIEW_ONLY_FEATURES.has(f)) {
          next[f] = { ...baseOf(f), view: checked, create: false, edit: false, delete: false };
        } else {
          next[f] = {
            view: checked,
            create: supportsCreateAction(f) ? checked : false,
            edit: supportsEditAction(f) ? checked : false,
            delete: supportsDeleteAction(f) ? checked : false,
          };
        }
      }
      if (item.exportLeaf) {
        const f = item.exportLeaf.feature;
        next[f] = { ...baseOf(f), view: checked, create: false, edit: false, delete: false };
      }
      return next;
    });
  };

  const collectProviderParentSlots = (item) => {
    const slots = [];
    if (item.providerLeaf) {
      for (const k of getApplicableKeysForRow(item.providerLeaf.feature)) {
        slots.push({ feature: item.providerLeaf.feature, key: k });
      }
    }
    if (item.ratingLeaf) slots.push({ feature: item.ratingLeaf.feature, key: "view" });
    return slots;
  };

  const isProviderParentAllSelected = (item) => {
    const slots = collectProviderParentSlots(item);
    if (!slots.length) return false;
    return slots.every(({ feature, key }) => !!getPermission(feature)[key]);
  };

  const isProviderParentSomeSelected = (item) => {
    const slots = collectProviderParentSlots(item);
    const n = slots.filter(({ feature, key }) => !!getPermission(feature)[key]).length;
    return n > 0 && n < slots.length;
  };

  const onToggleProviderParentAll = (item, checked) => {
    if (!canEditDepartmentPage) return;
    setPermissionDraft((prev) => {
      const next = { ...prev };
      const baseOf = (f) => {
        if (next[f]) return { ...next[f] };
        const x = activeDivision?.permissions?.find((p) => p.feature === f);
        return x
          ? {
              view: !!x.view,
              create: !!x.create,
              edit: !!x.edit,
              delete: !!x.delete,
            }
          : { view: false, create: false, edit: false, delete: false };
      };
      if (item.providerLeaf) {
        const f = item.providerLeaf.feature;
        next[f] = {
          view: checked,
          create: supportsCreateAction(f) ? checked : false,
          edit: supportsEditAction(f) ? checked : false,
          delete: supportsDeleteAction(f) ? checked : false,
        };
      }
      if (item.ratingLeaf) {
        const f = item.ratingLeaf.feature;
        next[f] = { ...baseOf(f), view: checked, create: false, edit: false, delete: false };
      }
      return next;
    });
  };

  const collectCustomerParentSlots = (item) => {
    const slots = [];
    for (const k of getApplicableKeysForRow(item.mainFeature)) {
      slots.push({ feature: item.mainFeature, key: k });
    }
    for (const leaf of item.filterLeaves || []) {
      slots.push({ feature: leaf.feature, key: "view" });
    }
    return slots;
  };

  const isCustomerParentAllSelected = (item) => {
    const slots = collectCustomerParentSlots(item);
    if (!slots.length) return false;
    return slots.every(({ feature, key }) => !!getPermission(feature)[key]);
  };

  const isCustomerParentSomeSelected = (item) => {
    const slots = collectCustomerParentSlots(item);
    const n = slots.filter(({ feature, key }) => !!getPermission(feature)[key]).length;
    return n > 0 && n < slots.length;
  };

  const onToggleCustomerParentAll = (item, checked) => {
    if (!canEditDepartmentPage) return;
    setPermissionDraft((prev) => {
      const next = { ...prev };
      const baseOf = (f) => {
        if (next[f]) return { ...next[f] };
        const x = findActiveDivisionPermissionRow(f);
        return x
          ? {
              view: !!x.view,
              create: !!x.create,
              edit: !!x.edit,
              delete: !!x.delete,
            }
          : { view: false, create: false, edit: false, delete: false };
      };
      const f = item.mainFeature;
      next[f] = {
        view: checked,
        create: supportsCreateAction(f) ? checked : false,
        edit: supportsEditAction(f) ? checked : false,
        delete: supportsDeleteAction(f) ? checked : false,
      };
      for (const leaf of item.filterLeaves || []) {
        next[leaf.feature] = { ...baseOf(leaf.feature), view: checked, create: false, edit: false, delete: false };
      }
      return next;
    });
  };

  const renderOrderLeadingViewOnlyRow = (child, rowStt, opts = {}) => {
    const permission = getPermission(child.feature);
    const checkTitle = opts.checkTitle;
    const rowKey = opts.rowKey ?? child.rowKey ?? child.feature;
    const depth = opts.depth ?? 3;
    return (
      <tr key={rowKey}>
        <td className="text-center">{rowStt}</td>
        <td className="dept-perm-name-cell">
          <DeptPermNameRow
            depth={depth}
            checkPrimary={
              <Form.Check
                type="checkbox"
                className="dept-perm-inline-check m-0"
                checked={!!permission.view}
                disabled={!canEditDepartmentPage}
                title={checkTitle}
                aria-label={checkTitle ? `${child.label}: ${checkTitle}` : child.label}
                onChange={(e) => onTogglePermission(child.feature, "view", e.target.checked)}
              />
            }
            label={child.label}
          />
        </td>
        <td className="text-center"></td>
        <td></td>
        <td className="text-center"></td>
        <td className="text-center"></td>
      </tr>
    );
  };

  const renderProductExportRow = (child, rowStt) => {
    const permission = getPermission(child.feature);
    return (
      <tr key={child.feature}>
        <td className="text-center">{rowStt}</td>
        <td className="dept-perm-name-cell">
          <DeptPermNameRow
            depth={3}
            checkPrimary={
              <Form.Check
                type="checkbox"
                className="dept-perm-inline-check m-0"
                checked={!!permission.view}
                disabled={!canEditDepartmentPage}
                onChange={(e) => onTogglePermission(child.feature, "view", e.target.checked)}
              />
            }
            label={child.label}
          />
        </td>
        <td className="text-center"></td>
        <td></td>
        <td className="text-center"></td>
        <td className="text-center"></td>
      </tr>
    );
  };

  const renderPermissionLeafRow = (child, rowStt, opts = {}) => {
    const permission = getPermission(child.feature);
    const isViewOnlyFeature =
      ORDER_VIEW_ONLY_FEATURES.has(child.feature) || ACCOUNTING_VIEW_ONLY_FEATURES.has(child.feature);
    const isLevel3OrderFeature = ORDER_LEVEL3_FEATURES.has(child.feature);
    const depth = opts.depth === 3 ? 3 : 2;

    const checkPrimary =
      hasAnyColumnPermissionCheckbox(child.feature) ? (
        <RowSelectAllCheckbox
          checked={isRowAllSelected(child.feature)}
          indeterminate={isRowSomeSelected(child.feature)}
          disabled={!canEditDepartmentPage}
          onChange={(e) => onToggleRowAll(child.feature, e.target.checked)}
          ariaLabel={`Chọn tất cả quyền: ${child.label}`}
          title="Chọn tất cả quyền trong dòng"
        />
      ) : null;

    const checkSecondary =
      isLevel3OrderFeature ? (
        <Form.Check
          type="checkbox"
          className="dept-perm-inline-check m-0"
          checked={!!permission.view}
          disabled={!canEditDepartmentPage}
          onChange={(e) => onTogglePermission(child.feature, "view", e.target.checked)}
        />
      ) : null;

    return (
      <tr key={child.rowKey ?? child.feature}>
        <td className="text-center">{rowStt}</td>
        <td className="dept-perm-name-cell">
          <DeptPermNameRow
            depth={depth}
            checkPrimary={checkPrimary}
            checkSecondary={checkSecondary}
            label={child.label}
          />
        </td>
        {["view", "create", "edit", "delete"].map((key) => (
          <td key={key} className="text-center">
            {!isLevel3OrderFeature &&
              !(key === "create" && !supportsCreateAction(child.feature)) &&
              !(key === "edit" && !supportsEditAction(child.feature)) &&
              !(key === "delete" && !supportsDeleteAction(child.feature)) && (
                <Form.Check
                  type="checkbox"
                  checked={
                    key === "view"
                      ? !!permission[key]
                      : isViewOnlyFeature
                        ? false
                        : !permission.view
                          ? false
                          : !!permission[key]
                  }
                  disabled={
                    !canEditDepartmentPage ||
                    (isViewOnlyFeature && key !== "view") ||
                    (key !== "view" && !isViewOnlyFeature && !permission.view)
                  }
                  onChange={(e) => onTogglePermission(child.feature, key, e.target.checked)}
                />
              )}
          </td>
        ))}
      </tr>
    );
  };

  const onSaveSettings = async () => {
    if (!canEditDepartmentPage) return;
    if (!activeDivision) return;
    const aid = Number(activeDivision.id);
    const permissions = allFeatures.map((feature) => {
      if (feature === ORDER_DIVISION_FEATURE) {
        const d = deriveOrderAggregatePermission(getPermission);
        return {
          feature,
          view: d.view,
          create: d.create,
          edit: d.edit,
          delete: d.delete,
        };
      }
      const p = getPermission(feature);
      const isViewOnlyFeature =
        ORDER_VIEW_ONLY_FEATURES.has(feature) || ACCOUNTING_VIEW_ONLY_FEATURES.has(feature);
      const isExportOnlyFeature =
        PRODUCT_VIEW_FLAG_ONLY_FEATURES.has(feature) ||
        CUSTOMER_FILTER_VIEW_ONLY_FEATURES.has(feature) ||
        isOrderExcelStyleViewOnlyFeature(feature);
      const canEdit = supportsEditAction(feature);
      const canDelete = supportsDeleteAction(feature);
      const canCreate = supportsCreateAction(feature);
      const pNorm = stripCrudWithoutView(feature, p);
      const viewOn = !!pNorm.view;
      return {
        feature,
        view: viewOn,
        create: isExportOnlyFeature || !canCreate || !viewOn ? false : !!pNorm.create,
        edit: isViewOnlyFeature || isExportOnlyFeature || !canEdit || !viewOn ? false : !!pNorm.edit,
        delete: isExportOnlyFeature || !canDelete || !viewOn ? false : !!pNorm.delete,
      };
    });
    const removalIdsSnapshot = [...pendingRemovalIds];
    const scopeEditsSnapshot = { ...accessScopeEdits };
    try {
      await axiosInstance.put(
        `/api/users/departments/${departmentId}/divisions/${activeDivision.id}/permissions`,
        { permissions }
      );
      for (const u of visibleDivisionUsers) {
        if (u.isAdmin === true) continue;
        const nextScope = scopeEditsSnapshot[u.id];
        if (nextScope === undefined) continue;
        const current = effectiveDataAccessScope(u);
        if (nextScope === current) continue;
        const res = await axiosInstance.put(
          "/api/users/update",
          buildDivisionUserUpdatePayload(u, { dataAccessScope: nextScope })
        );
        if (!res?.data?.success) {
          toast.error(res?.data?.message || `Không lưu phạm vi cho user #${u.id}.`);
          await loadDepartments();
          await loadAdminUsers();
          return;
        }
      }
      if (removalIdsSnapshot.length > 0) {
        for (const uid of removalIdsSnapshot) {
          const user = adminUsers.find((u) => Number(u.id) === Number(uid));
          if (!user) continue;
          const merged = userDivisionIdList(user).filter((d) => d !== aid);
          const res = await axiosInstance.put(
            "/api/users/update",
            buildDivisionUserUpdatePayload(user, {
              divisionIds: merged,
              divisionId: merged.length ? merged[0] : null,
            })
          );
          if (!res?.data?.success) {
            toast.error(res?.data?.message || `Không gỡ được user #${uid}.`);
            await loadDepartments();
            await loadAdminUsers();
            return;
          }
        }
        setPendingRemovalIds([]);
      }
      setAccessScopeEdits({});
      await loadDepartments();
      await loadAdminUsers();
      const scopeChanged = Object.keys(scopeEditsSnapshot).length > 0;
      toast.success(
        removalIdsSnapshot.length || scopeChanged
          ? "Đã lưu cài đặt bộ phận và cập nhật danh sách user."
          : "Đã lưu cài đặt bộ phận."
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || "Lưu thất bại.");
    }
  };

  const onAddUser = async () => {
    if (!canEditDepartmentPage) return;
    if (!activeDivision || !selectedUserId) return;
    const targetId = Number(selectedUserId);
    const user = adminUsers.find((u) => Number(u.id) === targetId);
    if (!user) return;
    const mergedDivIds = [...new Set([...userDivisionIdList(user), Number(activeDivision.id)])];
    await axiosInstance.put(
      "/api/users/update",
      buildDivisionUserUpdatePayload(user, {
        divisionIds: mergedDivIds,
        divisionId: mergedDivIds.length ? mergedDivIds[0] : null,
      })
    );
    await loadAdminUsers();
    setPendingRemovalIds((prev) => prev.filter((id) => id !== targetId));
    setSelectedUserId("");
    setShowUserPicker(false);
  };

  const openCreateDivision = () => {
    if (!canEditDepartmentPage) return;
    setEditingDivision(null);
    setShowDivisionEditor(true);
  };

  const openEditDivision = (division) => {
    if (!canEditDepartmentPage) return;
    setEditingDivision({ id: division.id, name: division.name });
    setShowDivisionEditor(true);
  };

  const onSaveDivision = async (model) => {
    const crudDept = effectiveDeptIdForCrud;
    if (model.id) {
      await axiosInstance.put(`/api/users/departments/${crudDept}/divisions/${model.id}`, model);
    } else {
      await axiosInstance.post(`/api/users/departments/${crudDept}/divisions`, model);
    }
    const reloaded = await loadDepartments();
    setShowDivisionEditor(false);
    const updatedDepartment = reloaded.find((dep) => dep.id === crudDept);
    if (model.id) {
      setSelectedDivisionId(model.id);
    } else {
      const added = updatedDepartment?.divisions?.[updatedDepartment.divisions.length - 1];
      if (added) setSelectedDivisionId(added.id);
    }
    toast.success(model.id ? "Đã cập nhật bộ phận." : "Đã thêm bộ phận.");
  };

  const onDivisionDeleted = async () => {
    const reloaded = await loadDepartments();
    const updatedDepartment = reloaded.find((dep) => dep.id === departmentId);
    const divs = updatedDepartment?.divisions || [];
    setSelectedDivisionId(divs.length ? divs[0].id : null);
  };

  useImperativeHandle(ref, () => ({
    save: () => {
      void onSaveSettings();
    },
    openCreateDivision,
    openEditDivision,
  }));

  const headerTitle =
    panelHeaderTitle ??
    (activeDivision ? formatDivisionLabelWithUserCount(activeDivision) : "Chọn bộ phận");

  return (
    <>
            <div className="card h-100 d-flex flex-column enterprise-permission-editor-panel">
              <div className="card-header fw-semibold py-3">{headerTitle}</div>
              <div className="card-body">
                {!activeDivision && <div className="text-muted">Vui lòng chọn một bộ phận bên trái.</div>}
                {activeDivision && (
                  <div className="row g-3">
                    <div className="col-xl-7">
                      <DataTable maxHeight="calc(100vh - 260px)">
                      <table className="table table-bordered table-sm dept-perm-table">
                        <thead>
                          <tr>
                            <th style={{ width: 50 }} className="text-center">STT</th>
                            <th>Tên chức năng</th>
                            <th style={{ width: 60 }} className="text-center">Xem</th>
                            <th style={{ width: 60 }}>Thêm</th>
                            <th style={{ width: 60 }} className="text-center">Sửa</th>
                            <th style={{ width: 60 }} className="text-center">Xóa</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            let stt = 0;
                            return featureTree.map((group) => (
                              <Fragment key={group.parent}>
                                <tr className="dept-perm-root-row">
                                  <td className="text-center"></td>
                                  <td className="dept-perm-name-cell fw-semibold">
                                    {group.parent === CSKH_DIVISION_PARENT ? (
                                      <DeptPermNameRow
                                        depth={2}
                                        expandSlot={
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm text-dark dept-perm-expand-btn"
                                            aria-expanded={cskhModuleExpanded}
                                            title={cskhModuleExpanded ? "Thu gọn" : "Mở rộng"}
                                            onClick={() => setCskhModuleExpanded((v) => !v)}
                                          >
                                            {cskhModuleExpanded ? (
                                              <FaChevronDown size={14} />
                                            ) : (
                                              <FaChevronRight size={14} />
                                            )}
                                          </button>
                                        }
                                        label={formatPermissionGroupHeader(group.parent)}
                                        labelClassName="fw-semibold"
                                      />
                                    ) : (
                                      <DeptPermNameRow
                                        depth={2}
                                        expandSlot={
                                          <button
                                            type="button"
                                            className="btn btn-link btn-sm text-dark dept-perm-expand-btn"
                                            aria-expanded={isRootGroupExpanded(group.parent)}
                                            title={isRootGroupExpanded(group.parent) ? "Thu gọn" : "Mở rộng"}
                                            onClick={() => toggleRootGroup(group.parent)}
                                          >
                                            {isRootGroupExpanded(group.parent) ? (
                                              <FaChevronDown size={14} />
                                            ) : (
                                              <FaChevronRight size={14} />
                                            )}
                                          </button>
                                        }
                                        label={formatPermissionGroupHeader(group.parent)}
                                        labelClassName="fw-semibold"
                                      />
                                    )}
                                  </td>
                                  <td className="text-center"></td>
                                  <td></td>
                                  <td className="text-center"></td>
                                  <td className="text-center"></td>
                                </tr>
                                {isRootGroupExpanded(group.parent) && group.children.map((item) => {
                                  if (item.kind === "customer-root") {
                                    stt += 1;
                                    const parentStt = stt;
                                    const mainFeature = item.mainFeature;
                                    const mainPerm = getPermission(mainFeature);
                                    return (
                                      <Fragment key="khach-hang-module">
                                        <tr className="table-light">
                                          <td className="text-center align-top">{parentStt}</td>
                                          <td className="align-top dept-perm-name-cell">
                                            <div className="py-1">
                                              <DeptPermNameRow
                                                depth={2}
                                                expandSlot={
                                                  <button
                                                    type="button"
                                                    className="btn btn-link btn-sm text-dark dept-perm-expand-btn"
                                                    aria-expanded={customerModuleExpanded}
                                                    title={customerModuleExpanded ? "Thu gọn" : "Mở rộng"}
                                                    onClick={() => setCustomerModuleExpanded((v) => !v)}
                                                  >
                                                    {customerModuleExpanded ? (
                                                      <FaChevronDown size={14} />
                                                    ) : (
                                                      <FaChevronRight size={14} />
                                                    )}
                                                  </button>
                                                }
                                                checkPrimary={
                                                  <RowSelectAllCheckbox
                                                    checked={isCustomerParentAllSelected(item)}
                                                    indeterminate={isCustomerParentSomeSelected(item)}
                                                    disabled={!canEditDepartmentPage}
                                                    onChange={(e) =>
                                                      onToggleCustomerParentAll(item, e.target.checked)
                                                    }
                                                    ariaLabel="Chọn tất cả quyền các mục con: Khách hàng"
                                                    title="Chọn tất cả quyền Khách hàng và các bộ lọc"
                                                  />
                                                }
                                                label={item.label}
                                                labelClassName="fw-semibold"
                                              />
                                            </div>
                                          </td>
                                          {["view", "create", "edit", "delete"].map((key) => (
                                            <td key={key} className="text-center align-top">
                                              {!(key === "create" && !supportsCreateAction(mainFeature)) &&
                                                !(key === "edit" && !supportsEditAction(mainFeature)) &&
                                                !(key === "delete" && !supportsDeleteAction(mainFeature)) && (
                                                  <Form.Check
                                                    type="checkbox"
                                                    checked={
                                                      key === "view"
                                                        ? !!mainPerm[key]
                                                        : !mainPerm.view
                                                          ? false
                                                          : !!mainPerm[key]
                                                    }
                                                    disabled={
                                                      !canEditDepartmentPage ||
                                                      (key !== "view" && !mainPerm.view)
                                                    }
                                                    onChange={(e) =>
                                                      onTogglePermission(mainFeature, key, e.target.checked)
                                                    }
                                                  />
                                                )}
                                            </td>
                                          ))}
                                        </tr>
                                        {customerModuleExpanded &&
                                          item.filterLeaves.map((leaf) => {
                                            stt += 1;
                                            return renderOrderLeadingViewOnlyRow(leaf, stt, {
                                              depth: 3,
                                              checkTitle: `Hiển thị nút lọc «${leaf.label}» trên Danh sách khách hàng`,
                                            });
                                          })}
                                      </Fragment>
                                    );
                                  }
                                  if (item.kind === "order-root") {
                                    stt += 1;
                                    const parentStt = stt;
                                    const moduleId = item.id || "order-vanchuyen";
                                    const moduleExpanded = orderModuleExpandedById[moduleId] === true;
                                    const isDisplayColumnsModule = moduleId === "order-display-columns";
                                    return (
                                      <Fragment key={moduleId}>
                                        <tr className="table-light">
                                          <td className="text-center align-top">{parentStt}</td>
                                          <td className="align-top dept-perm-name-cell">
                                            <div className="py-1">
                                              <DeptPermNameRow
                                                depth={2}
                                                expandSlot={
                                                  <button
                                                    type="button"
                                                    className="btn btn-link btn-sm text-dark dept-perm-expand-btn"
                                                    aria-expanded={moduleExpanded}
                                                    title={moduleExpanded ? "Thu gọn" : "Mở rộng"}
                                                    onClick={() =>
                                                      setOrderModuleExpandedById((prev) => ({
                                                        ...prev,
                                                        [moduleId]: !moduleExpanded,
                                                      }))
                                                    }
                                                  >
                                                    {moduleExpanded ? (
                                                      <FaChevronDown size={14} />
                                                    ) : (
                                                      <FaChevronRight size={14} />
                                                    )}
                                                  </button>
                                                }
                                                checkPrimary={
                                                  <RowSelectAllCheckbox
                                                    checked={isOrderModuleAllSelected(item)}
                                                    indeterminate={isOrderModuleSomeSelected(item)}
                                                    disabled={!canEditDepartmentPage}
                                                    onChange={(e) =>
                                                      onToggleOrderModuleAll(item, e.target.checked)
                                                    }
                                                    ariaLabel={`Chọn tất cả quyền các mục con: ${item.title}`}
                                                    title={
                                                      isDisplayColumnsModule
                                                        ? "Bật/tắt Xem cho tất cả cột hiển thị (áp dụng mọi đơn: mua hộ và vận chuyển)"
                                                        : `Chọn tất cả quyền trong nhóm ${item.title}`
                                                    }
                                                  />
                                                }
                                                label={item.title}
                                                labelClassName="fw-semibold"
                                                labelTitle={
                                                  isDisplayColumnsModule
                                                    ? "Áp dụng cho tất cả đơn hàng trên danh sách (mua hộ và vận chuyển)"
                                                    : undefined
                                                }
                                              />
                                            </div>
                                          </td>
                                          <td className="text-center align-top"></td>
                                          <td className="align-top"></td>
                                          <td className="text-center align-top"></td>
                                          <td className="text-center align-top"></td>
                                        </tr>
                                        {moduleExpanded &&
                                          item.inner.map((sub) => {
                                            if (sub.kind === "leaf") {
                                              stt += 1;
                                              return renderPermissionLeafRow(sub, stt, { depth: 3 });
                                            }
                                            if (sub.kind === "order-excel-folder") {
                                              stt += 1;
                                              const excelFolderStt = stt;
                                              const isQuickUpdateFolder = sub.id === "order-quick-update";
                                              const folderExpanded = isQuickUpdateFolder
                                                ? orderQuickUpdateExpanded
                                                : orderExcelExportsExpanded;
                                              const setFolderExpanded = isQuickUpdateFolder
                                                ? setOrderQuickUpdateExpanded
                                                : setOrderExcelExportsExpanded;
                                              return (
                                                <Fragment key={sub.id}>
                                                  <tr className="table-light">
                                                    <td className="text-center align-top">{excelFolderStt}</td>
                                                    <td className="align-top dept-perm-name-cell">
                                                      <div className="py-1">
                                                        <DeptPermNameRow
                                                          depth={3}
                                                          expandSlot={
                                                            <button
                                                              type="button"
                                                              className="btn btn-link btn-sm text-dark dept-perm-expand-btn"
                                                              aria-expanded={folderExpanded}
                                                              title={folderExpanded ? "Thu gọn" : "Mở rộng"}
                                                              onClick={() => setFolderExpanded((v) => !v)}
                                                            >
                                                              {folderExpanded ? (
                                                                <FaChevronDown size={14} />
                                                              ) : (
                                                                <FaChevronRight size={14} />
                                                              )}
                                                            </button>
                                                          }
                                                          checkPrimary={
                                                            <RowSelectAllCheckbox
                                                              checked={isOrderExcelFolderAllSelected(sub)}
                                                              indeterminate={isOrderExcelFolderSomeSelected(sub)}
                                                              disabled={!canEditDepartmentPage}
                                                              onChange={(e) =>
                                                                onToggleOrderExcelFolderAll(
                                                                  sub,
                                                                  e.target.checked
                                                                )
                                                              }
                                                              ariaLabel={`Chọn tất cả: ${sub.title}`}
                                                              title={
                                                                isQuickUpdateFolder
                                                                  ? "Bật/tắt Xem cho Cập nhật nhanh"
                                                                  : "Bật/tắt tất cả mục xuất Excel"
                                                              }
                                                            />
                                                          }
                                                          label={sub.title}
                                                          labelClassName="fw-semibold"
                                                        />
                                                        {folderExpanded && (
                                                          <div className="dept-perm-l3-stack">
                                                            {sub.children.map((leaf) => {
                                                              const permission = getPermission(leaf.feature);
                                                              return (
                                                                <DeptPermNameRow
                                                                  key={leaf.feature}
                                                                  depth={4}
                                                                  checkPrimary={
                                                                    <Form.Check
                                                                      type="checkbox"
                                                                      className="dept-perm-inline-check m-0"
                                                                      checked={!!permission.view}
                                                                      disabled={!canEditDepartmentPage}
                                                                      onChange={(e) =>
                                                                        onTogglePermission(
                                                                          leaf.feature,
                                                                          "view",
                                                                          e.target.checked
                                                                        )
                                                                      }
                                                                    />
                                                                  }
                                                                  label={leaf.label}
                                                                />
                                                              );
                                                            })}
                                                          </div>
                                                        )}
                                                      </div>
                                                    </td>
                                                    <td className="text-center align-top"></td>
                                                    <td className="align-top"></td>
                                                    <td className="text-center align-top"></td>
                                                    <td className="text-center align-top"></td>
                                                  </tr>
                                                </Fragment>
                                              );
                                            }
                                            return null;
                                          })}
                                      </Fragment>
                                    );
                                  }
                                  if (item.kind === "product-root") {
                                    stt += 1;
                                    const parentStt = stt;
                                    return (
                                      <Fragment key="san-pham-module">
                                        <tr className="table-light">
                                          <td className="text-center align-top">{parentStt}</td>
                                          <td className="align-top dept-perm-name-cell">
                                            <div className="py-1">
                                              <DeptPermNameRow
                                                depth={2}
                                                expandSlot={
                                                  <button
                                                    type="button"
                                                    className="btn btn-link btn-sm text-dark dept-perm-expand-btn"
                                                    aria-expanded={productModuleExpanded}
                                                    title={productModuleExpanded ? "Thu gọn" : "Mở rộng"}
                                                    onClick={() =>
                                                      setProductModuleExpanded((v) => !v)
                                                    }
                                                  >
                                                    {productModuleExpanded ? (
                                                      <FaChevronDown size={14} />
                                                    ) : (
                                                      <FaChevronRight size={14} />
                                                    )}
                                                  </button>
                                                }
                                                checkPrimary={
                                                  <RowSelectAllCheckbox
                                                    checked={isProductParentAllSelected(item)}
                                                    indeterminate={isProductParentSomeSelected(item)}
                                                    disabled={!canEditDepartmentPage}
                                                    onChange={(e) =>
                                                      onToggleProductParentAll(item, e.target.checked)
                                                    }
                                                    ariaLabel="Chọn tất cả quyền các mục con: Danh sách sản phẩm"
                                                    title="Chọn tất cả quyền của SP cá nhân, SP dùng chung và Xuất Excel"
                                                  />
                                                }
                                                label={PRODUCT_UI_PARENT_LABEL}
                                                labelClassName="fw-semibold"
                                              />
                                            </div>
                                          </td>
                                          <td className="text-center align-top"></td>
                                          <td className="align-top"></td>
                                          <td className="text-center align-top"></td>
                                          <td className="text-center align-top"></td>
                                        </tr>
                                        {productModuleExpanded && (
                                          <>
                                            {item.fullLeaves.map((leaf) => {
                                              stt += 1;
                                              return renderPermissionLeafRow(leaf, stt, { depth: 3 });
                                            })}
                                            {item.exportLeaf &&
                                              (() => {
                                                stt += 1;
                                                return renderProductExportRow(item.exportLeaf, stt);
                                              })()}
                                          </>
                                        )}
                                      </Fragment>
                                    );
                                  }
                                  if (item.kind === "provider-root") {
                                    stt += 1;
                                    const parentStt = stt;
                                    const providerFeature = item.providerLeaf?.feature;
                                    const providerPerm = providerFeature
                                      ? getPermission(providerFeature)
                                      : null;
                                    return (
                                      <Fragment key="nha-cung-cap-module">
                                        <tr className="table-light">
                                          <td className="text-center align-top">{parentStt}</td>
                                          <td className="align-top dept-perm-name-cell">
                                            <div className="py-1">
                                              <DeptPermNameRow
                                                depth={2}
                                                expandSlot={
                                                  item.ratingLeaf ? (
                                                    <button
                                                      type="button"
                                                      className="btn btn-link btn-sm text-dark dept-perm-expand-btn"
                                                      aria-expanded={providerModuleExpanded}
                                                      title={providerModuleExpanded ? "Thu gọn" : "Mở rộng"}
                                                      onClick={() =>
                                                        setProviderModuleExpanded((v) => !v)
                                                      }
                                                    >
                                                      {providerModuleExpanded ? (
                                                        <FaChevronDown size={14} />
                                                      ) : (
                                                        <FaChevronRight size={14} />
                                                      )}
                                                    </button>
                                                  ) : null
                                                }
                                                checkPrimary={
                                                  <RowSelectAllCheckbox
                                                    checked={isProviderParentAllSelected(item)}
                                                    indeterminate={isProviderParentSomeSelected(item)}
                                                    disabled={!canEditDepartmentPage}
                                                    onChange={(e) =>
                                                      onToggleProviderParentAll(item, e.target.checked)
                                                    }
                                                    ariaLabel="Chọn tất cả quyền các mục con: Nhà cung cấp"
                                                    title="Chọn tất cả quyền Nhà cung cấp và Bình luận - Đánh giá"
                                                  />
                                                }
                                                label={item.label}
                                                labelClassName="fw-semibold"
                                              />
                                            </div>
                                          </td>
                                          {providerFeature && providerPerm
                                            ? ["view", "create", "edit", "delete"].map((key) => (
                                                <td key={key} className="text-center align-top">
                                                  {!(key === "create" && !supportsCreateAction(providerFeature)) &&
                                                    !(key === "edit" && !supportsEditAction(providerFeature)) &&
                                                    !(key === "delete" && !supportsDeleteAction(providerFeature)) && (
                                                      <Form.Check
                                                        type="checkbox"
                                                        checked={
                                                          key === "view"
                                                            ? !!providerPerm[key]
                                                            : !providerPerm.view
                                                              ? false
                                                              : !!providerPerm[key]
                                                        }
                                                        disabled={
                                                          !canEditDepartmentPage ||
                                                          (key !== "view" && !providerPerm.view)
                                                        }
                                                        onChange={(e) =>
                                                          onTogglePermission(
                                                            providerFeature,
                                                            key,
                                                            e.target.checked
                                                          )
                                                        }
                                                      />
                                                    )}
                                                </td>
                                              ))
                                            : [
                                                <td key="v" className="text-center align-top" />,
                                                <td key="c" className="align-top" />,
                                                <td key="e" className="text-center align-top" />,
                                                <td key="d" className="text-center align-top" />,
                                              ]}
                                        </tr>
                                        {providerModuleExpanded &&
                                          item.ratingLeaf &&
                                          (() => {
                                            stt += 1;
                                            return renderProductExportRow(item.ratingLeaf, stt);
                                          })()}
                                      </Fragment>
                                    );
                                  }
                                  if (item.kind === "leaf" && CSKH_LEADING_ONLY.has(item.feature)) {
                                    if (!cskhModuleExpanded) return null;
                                    stt += 1;
                                    return renderOrderLeadingViewOnlyRow(item, stt);
                                  }
                                  if (item.kind === "leaf" && THONG_BAO_LEADING_ONLY.has(item.feature)) {
                                    stt += 1;
                                    return renderOrderLeadingViewOnlyRow(item, stt, {
                                      checkTitle: THONG_BAO_HINTS[item.feature],
                                    });
                                  }
                                  if (
                                    item.kind === "leaf" &&
                                    item.feature === PROVIDER_RATING_COMMENT_DIVISION_FEATURE &&
                                    item.rowKey === THONG_BAO_ROW_SYNTHETIC_RATING
                                  ) {
                                    stt += 1;
                                    return renderOrderLeadingViewOnlyRow(item, stt, {
                                      checkTitle:
                                        "Cùng quyền với «Bình luận - Đánh giá» dưới nhóm Nhà cung cấp (Sản phẩm).",
                                      rowKey: item.rowKey,
                                      depth: 3,
                                    });
                                  }
                                  stt += 1;
                                  return renderPermissionLeafRow(item, stt, {
                                    depth: item.deptPermDepth === 3 ? 3 : 2,
                                  });
                                })}
                              </Fragment>
                            ));
                          })()}
                        </tbody>
                      </table>
                      </DataTable>
                    </div>
                    <div className="col-xl-5">
                      <div className="d-flex justify-content-between align-items-start mb-2 gap-2 flex-wrap">
                        <div>
                          <h6 className="fw-semibold mb-0">Danh sách users</h6>
                        </div>
                        {canEditDepartmentPage && (
                          <Button
                            size="sm"
                            className="d-inline-flex align-items-center gap-1 flex-shrink-0"
                            onClick={() => setShowUserPicker(true)}
                          >
                            <FaUserPlus size={12} />
                            Thêm user
                          </Button>
                        )}
                      </div>
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0 align-middle dept-perm-users-table">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 52 }} className="text-center">
                                STT
                              </th>
                              <th
                                className="dept-perm-scope-col text-center"
                                title="Phạm vi truy cập"
                              >
                                Phạm vi
                              </th>
                              <th>Họ tên</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleDivisionUsers.map((u, index) => {
                              const loginName = u.username || u.email || "—";
                              return (
                              <tr key={u.id} className="dept-perm-user-row">
                                <td className="text-center">{index + 1}</td>
                                <td className="dept-perm-scope-col">
                                  {canEditDepartmentPage && u.isAdmin !== true ? (
                                    <Form.Select
                                      size="sm"
                                      className="dept-perm-scope-select"
                                      value={getUserAccessScope(u)}
                                      title="Phạm vi truy cập"
                                      onChange={(e) =>
                                        setAccessScopeEdits((prev) => ({
                                          ...prev,
                                          [u.id]: normalizeDataAccessScope(e.target.value),
                                        }))
                                      }
                                    >
                                      {DATA_ACCESS_SCOPE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  ) : (
                                    <span className="text-muted small" title="Phạm vi truy cập">
                                      {DATA_ACCESS_SCOPE_OPTIONS.find(
                                        (opt) => opt.value === getUserAccessScope(u)
                                      )?.label || "Cá nhân"}
                                    </span>
                                  )}
                                </td>
                                <td
                                  className={
                                    canEditDepartmentPage
                                      ? "position-relative text-start pe-4"
                                      : "text-start"
                                  }
                                >
                                  <Link
                                    to={`/admin-users?userId=${u.id}`}
                                    className="text-decoration-none dept-perm-user-name-link"
                                    title={`Tên đăng nhập: ${loginName} — Xem chi tiết user`}
                                  >
                                    {u.name || "—"}
                                  </Link>
                                  {canEditDepartmentPage && (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm text-secondary p-0 dept-perm-user-close-btn position-absolute top-50 translate-middle-y end-0 me-1"
                                      title="Gỡ khỏi bộ phận (lưu khi nhấn Lưu)"
                                      aria-label="Gỡ user khỏi bộ phận"
                                      onClick={() => queueRemoveUserFromDivision(u.id)}
                                    >
                                      <FiX size={18} strokeWidth={2.25} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                            })}
                            {!visibleDivisionUsers.length && (
                              <tr>
                                <td colSpan={3} className="text-center text-muted">
                                  Chưa có user trong bộ phận này.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {!hideFooterSave && (
                <div className="card-footer d-flex justify-content-end mt-auto border-top bg-white py-3">
                  {canEditDepartmentPage && (
                    <Button size="sm" className="d-inline-flex align-items-center gap-2" onClick={onSaveSettings}>
                      <FiSave size={16} />
                      Lưu
                    </Button>
                  )}
                </div>
              )}
            </div>

      <Modal show={showUserPicker} onHide={() => setShowUserPicker(false)} centered dialogClassName="department-modal">
        <Modal.Header closeButton><Modal.Title>Thêm user vào bộ phận</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
            <option value="">Chọn user</option>
            {selectableUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer className="gap-2">
          <Button
            size="sm"
            variant="secondary"
            className="d-inline-flex align-items-center gap-1"
            onClick={() => setShowUserPicker(false)}
          >
            <FiX size={14} />
            Hủy
          </Button>
          <Button
            size="sm"
            className="d-inline-flex align-items-center gap-1"
            onClick={onAddUser}
          >
            <FaUserPlus size={12} />
            Thêm
          </Button>
        </Modal.Footer>
      </Modal>

      <DivisionEditor
        show={showDivisionEditor}
        model={editingDivision}
        departmentId={effectiveDeptIdForCrud}
        departmentLabel={divisionEditorDepartmentLabel}
        canDelete={canEditDepartmentPage}
        onClose={() => setShowDivisionEditor(false)}
        onSave={onSaveDivision}
        onDeleted={onDivisionDeleted}
      />
    </>
  );
});

DivisionPermissionEditor.displayName = "DivisionPermissionEditor";

export default DivisionPermissionEditor;
